import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import axiosPrivate from 'config/axiosPrivate';
import {
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Button,
    Form,
    FormGroup,
    Label,
    Input,
    Nav,
    NavItem,
    NavLink,
    TabContent,
    TabPane,
} from 'reactstrap';
import CustomSelectDropdown from '../DropDown/CustomSelectDropdown';
import './UpdateProjectModal.css';

const llmTypeList = [
    { name: 'GPT', type: 'GPT' },
    { name: 'CLAUDE', type: 'CLAUDE' },
    { name: 'CLAUDE_GPT', type: 'CLAUDE_GPT' },
    { name: 'PERPLEXITY', type: 'PERPLEXITY' },
];

const UpdateProjectModal = ({ isOpen, onClose, order, t: tProp = null, onUpdate }) => {
    const { t: tHook } = useTranslation();
    const t = tProp || tHook;

    const contentFormatList = useMemo(() => [
        { name: t('Article court - 300 mots'), type: 'SHORT_ARTICLE' },
        { name: t('Article moyen - 600 mots'), type: 'MEDIUM_ARTICLE' },
        { name: t('Article long - 1000 mots'), type: 'LONG_ARTICLE' },
        { name: t('Youtube - 350 mots'), type: 'YOUTUBE' },
        { name: t('Linkedin - 200 mots'), type: 'LINKEDIN' },
        { name: t('X - 150 mots'), type: 'X' },
        { name: t('Personnalisé'), type: 'CUSTOM', isCustom: true },
    ], [t]);

    const [activeTab, setActiveTab] = useState('general');
    const [formData, setFormData] = useState({
        title: '',
        projectManager: null,
        deadline: '',
        textAmount: '',
        numberOfImages: '',
        aiAgent: null,
        aiName: '',
        llmType: null,
        wordPreset: null,
        customMinWords: '',
        customMaxWords: '',
        detailedBrief: '',
        semanticGap: '',
    });
    const [projectManagersList, setProjectManagersList] = useState([]);
    const [aiAgentsList, setAiAgentsList] = useState([]);
    const [availableWritersList, setAvailableWritersList] = useState([]);
    const [writerTasks, setWriterTasks] = useState([]);
    const [agentContentType, setAgentContentType] = useState('');
    const [isAgentContentTypeResolved, setIsAgentContentTypeResolved] = useState(false);

    const safeOrder = order || {};
    const orderType = (safeOrder.type || '').toUpperCase();
    const isPremiumProject = orderType === 'PREMIUM';
    const isSmartWithReview = orderType === 'SMART_WITH_REVIEW';
    const showAllocation = isPremiumProject || isSmartWithReview;
    const isImageOptionEnabled = !!safeOrder.hasImages;

    const gptLlmType = llmTypeList.find((item) => item.type === 'GPT') || { name: 'GPT', type: 'GPT' };

    // LLM verrouillé en GPT uniquement pour les projets utilisant l'agent IA YouTube
    const effectiveSelectedAgent = useMemo(() => {
        if (formData.aiAgent) return formData.aiAgent;
        if (!safeOrder.aiAgentId) return null;
        return aiAgentsList.find((a) => String(a.id) === String(safeOrder.aiAgentId)) || null;
    }, [formData.aiAgent, safeOrder.aiAgentId, aiAgentsList]);

    const isYoutubeAgent = useMemo(() => {
        if (String(agentContentType).toUpperCase() === 'YOUTUBE') return true;
        if (String(effectiveSelectedAgent?.typeContenu || '').toUpperCase() === 'YOUTUBE') return true;
        if (String(formData.wordPreset?.type || '').toUpperCase() === 'YOUTUBE') return true;
        if (String(safeOrder?.contentFormat || '').toUpperCase() === 'YOUTUBE') return true;
        return false;
    }, [
        agentContentType,
        effectiveSelectedAgent,
        formData.wordPreset,
        safeOrder?.contentFormat,
    ]);

    useEffect(() => {
        if (!isOpen) {
            setAgentContentType('');
            setIsAgentContentTypeResolved(false);
            return;
        }

        if (!safeOrder.aiAgentId) {
            setAgentContentType('');
            setIsAgentContentTypeResolved(true);
            return;
        }

        let cancelled = false;
        setIsAgentContentTypeResolved(false);

        axiosPrivate
            .get(`/smart-content/agent_ia/${safeOrder.aiAgentId}`)
            .then((response) => {
                if (cancelled) return;
                const agentType = String(
                    response?.data?.typeContenu || response?.data?.contentType || ''
                ).toUpperCase();
                setAgentContentType(agentType);
            })
            .catch(() => {
                if (!cancelled) setAgentContentType('');
            })
            .finally(() => {
                if (!cancelled) setIsAgentContentTypeResolved(true);
            });

        return () => {
            cancelled = true;
        };
    }, [isOpen, safeOrder.aiAgentId]);

    useEffect(() => {
        if (!showAllocation && activeTab === 'allocation') {
            setActiveTab('general');
        }
    }, [showAllocation, activeTab]);

    useEffect(() => {
        if (isOpen && order) {
            let mappedWordPreset = null;
            let customMinWords = '';
            let customMaxWords = '';

            if (order.contentFormat) {
                const contentFormatValue = typeof order.contentFormat === 'string'
                    ? order.contentFormat
                    : (order.contentFormat?.type || order.contentFormat);

                mappedWordPreset = contentFormatList.find(
                    (preset) => preset.type === contentFormatValue
                ) || null;

                if (mappedWordPreset?.isCustom) {
                    customMinWords = order.customMinWords || order.minWords || '';
                    customMaxWords = order.customMaxWords || order.maxWords || '';
                }
            }

            if (!mappedWordPreset) {
                const maxWordsValue = Number(order.customMaxWords || order.maxWords || 0);
                const minWordsValue = Number(order.customMinWords || order.minWords || 0);

                if (maxWordsValue > 0) {
                    const presetFromMaxWords = contentFormatList.find(
                        (preset) =>
                            !preset.isCustom &&
                            getMaxWordsFromPreset(preset.type) === maxWordsValue
                    );

                    if (presetFromMaxWords) {
                        mappedWordPreset = presetFromMaxWords;
                    } else {
                        mappedWordPreset = contentFormatList.find((p) => p.isCustom) || null;
                        customMaxWords = String(maxWordsValue);
                        customMinWords = minWordsValue > 0
                            ? String(minWordsValue)
                            : String(maxWordsValue);
                    }
                }
            }

            setFormData({
                title: order.title || '',
                projectManager: order.projectManager || null,
                deadline: order.deadline
                    ? new Date(order.deadline).toISOString().split('T')[0]
                    : '',
                textAmount: order.textAmount || '',
                numberOfImages: order.numberOfImages || '',
                aiAgent: null,
                aiName: order.aiName || order.aiAgent?.name || '',
                llmType:
                    mappedWordPreset?.type === 'YOUTUBE'
                        ? gptLlmType
                        : llmTypeList.find((item) => item.type === (order.llmType || '')) || null,
                wordPreset: mappedWordPreset,
                customMinWords,
                customMaxWords,
                detailedBrief: order.detailedBrief || '',
                semanticGap: order.semanticGap || '',
            });
        }
    }, [isOpen, order]);

    // Quand l'agent courant est YouTube, forcer le moteur LLM à GPT
    useEffect(() => {
        if (!isOpen) return;

        if (isYoutubeAgent) {
            setFormData((prev) => ({ ...prev, llmType: gptLlmType }));
            return;
        }

        // Revenir à la valeur enregistrée si on change d'agent
        const restoreType = safeOrder?.llmType || '';
        const restoreOption = llmTypeList.find((item) => item.type === restoreType) || null;
        if (restoreOption) {
            setFormData((prev) => ({ ...prev, llmType: restoreOption }));
        }
    }, [isOpen, isYoutubeAgent, safeOrder?.llmType, gptLlmType]);

    useEffect(() => {
        if (isOpen) {
            axiosPrivate
                .get('/user/external/project-managers')
                .then((res) => setProjectManagersList(res.data || []))
                .catch(() => setProjectManagersList([]));

            const websiteUrl =
                safeOrder.website?.urlSite || safeOrder.websiteUrl || '';

            console.log('Fetching AI agents for websiteUrl:', websiteUrl);

            if (websiteUrl) {
                const isTranslation = (safeOrder.textType || '')
                    .toUpperCase()
                    .includes('TRANSL');
                const endpoint = isTranslation
                    ? `/smart-content/agent_ia/traduction/all?websiteUrl=${encodeURIComponent(websiteUrl)}`
                    : `/smart-content/agent_ia/redaction/all?websiteUrl=${encodeURIComponent(websiteUrl)}`;

                console.log('Fetching AI agents from:', endpoint);

                axiosPrivate
                    .get(endpoint)
                    .then(async (res) => {
                        console.log('AI agents response:', res.data);
                        const agents = res.data || [];

                        if (safeOrder.aiAgentId && !agents.find(a => String(a.id) === String(safeOrder.aiAgentId))) {
                            try {
                                const agentResponse = await axiosPrivate.get(
                                    `/smart-content/agent_ia/${safeOrder.aiAgentId}`
                                );
                                const agentData = agentResponse.data || {};
                                const agentName = agentData.name || safeOrder.aiName || safeOrder.aiAgent?.name || `Agent ${safeOrder.aiAgentId}`;
                                agents.unshift({
                                    id: safeOrder.aiAgentId,
                                    name: agentName,
                                    typeContenu: agentData.typeContenu || null,
                                });
                            } catch (agentError) {
                                console.error('Error fetching agent details:', agentError);
                                agents.unshift({
                                    id: safeOrder.aiAgentId,
                                    name: safeOrder.aiName || safeOrder.aiAgent?.name || `Agent ${safeOrder.aiAgentId}`,
                                    typeContenu: null,
                                });
                            }
                        }

                        setAiAgentsList(agents);
                    })
                    .catch((error) => {
                        console.error('Error fetching AI agents:', error);
                        setAiAgentsList([]);
                    });
            } else {
                console.warn('No websiteUrl found, cannot fetch AI agents');
            }
        }
    }, [isOpen, safeOrder.website?.urlSite, safeOrder.websiteUrl, safeOrder.textType, safeOrder.aiAgentId, safeOrder.aiName]);

    useEffect(() => {
        if (isOpen && showAllocation) {
            axiosPrivate.get('/user/external/global-writers')
                .then((res) => setAvailableWritersList(res.data || []))
                .catch(() => setAvailableWritersList([]));
        }
    }, [isOpen, showAllocation]);

    // Récupérer les données depuis l'API /project/line/{projectId}
    useEffect(() => {
        if (isOpen && showAllocation && order?.id) {
            axiosPrivate.get(`/project/line/${order.id}`)
                .then((res) => {
                    const responseData = res.data || {};
                    const projectLines = responseData.projectLineDtos || [];

                    if (projectLines.length > 0) {
                        setWriterTasks(projectLines.map((line) => ({
                            id: line.writerProjectId || Date.now() + Math.random(),
                            writer: line.userMailDTO || null,
                            numberOfTexts: line.textAmount || '',
                            minWords: line.minWords != null ? String(line.minWords) : '',
                            maxWords: line.maxWords != null ? String(line.maxWords) : '',
                            deadline: line.deadline ? (typeof line.deadline === 'string' ? line.deadline.split('T')[0] : line.deadline) : '',
                        })));
                    } else if (order?.writerTasks && order.writerTasks.length > 0) {
                        setWriterTasks(order.writerTasks.map(task => ({
                            id: task.id || Date.now() + Math.random(),
                            writer: task.writer || null,
                            numberOfTexts: task.numberOfTexts || task.textAmount || '',
                            minWords: task.minWords != null ? String(task.minWords) : '',
                            maxWords: task.maxWords != null ? String(task.maxWords) : '',
                            deadline: task.deadline ? (typeof task.deadline === 'string' ? task.deadline.split('T')[0] : task.deadline) : '',
                        })));
                    } else if (order?.writers && order.writers.length > 0) {
                        setWriterTasks(order.writers.map((writerAssignment, index) => ({
                            id: writerAssignment.id || Date.now() + index,
                            writer: writerAssignment.writer || writerAssignment,
                            numberOfTexts: writerAssignment.textAmount || '',
                            minWords: writerAssignment.minWords != null ? String(writerAssignment.minWords) : '',
                            maxWords: writerAssignment.maxWords != null ? String(writerAssignment.maxWords) : '',
                            deadline: writerAssignment.deadline ? (typeof writerAssignment.deadline === 'string' ? writerAssignment.deadline.split('T')[0] : writerAssignment.deadline) : '',
                        })));
                    } else {
                        setWriterTasks([]);
                    }
                })
                .catch((error) => {
                    console.error('Erreur lors de la récupération des lignes du projet:', error);
                    if (order?.writerTasks && order.writerTasks.length > 0) {
                        setWriterTasks(order.writerTasks.map(task => ({
                            id: task.id || Date.now() + Math.random(),
                            writer: task.writer || null,
                            numberOfTexts: task.numberOfTexts || task.textAmount || '',
                            minWords: task.minWords != null ? String(task.minWords) : '',
                            maxWords: task.maxWords != null ? String(task.maxWords) : '',
                            deadline: task.deadline ? (typeof task.deadline === 'string' ? task.deadline.split('T')[0] : task.deadline) : '',
                        })));
                    } else if (order?.writers && order.writers.length > 0) {
                        setWriterTasks(order.writers.map((writerAssignment, index) => ({
                            id: writerAssignment.id || Date.now() + index,
                            writer: writerAssignment.writer || writerAssignment,
                            numberOfTexts: writerAssignment.textAmount || '',
                            minWords: writerAssignment.minWords != null ? String(writerAssignment.minWords) : '',
                            maxWords: writerAssignment.maxWords != null ? String(writerAssignment.maxWords) : '',
                            deadline: writerAssignment.deadline ? (typeof writerAssignment.deadline === 'string' ? writerAssignment.deadline.split('T')[0] : writerAssignment.deadline) : '',
                        })));
                    } else {
                        setWriterTasks([]);
                    }
                });
        } else if (isOpen && showAllocation && !order?.id) {
            setWriterTasks([]);
        }
    }, [isOpen, showAllocation, order?.id]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleSelectChange = (name, value) => {
        if (name === 'aiAgent') {
            const isYoutube = String(value?.typeContenu || '').toUpperCase() === 'YOUTUBE';
            setFormData((prev) => ({
                ...prev,
                aiAgent: value,
                llmType: isYoutube ? gptLlmType : prev.llmType,
            }));
            if (value?.typeContenu) {
                setAgentContentType(String(value.typeContenu).toUpperCase());
            }
            return;
        }

        if (name === 'wordPreset') {
            const isYoutube = String(value?.type || '').toUpperCase() === 'YOUTUBE';
            setFormData((prev) => ({
                ...prev,
                wordPreset: value,
                llmType: isYoutube ? gptLlmType : prev.llmType,
            }));
            return;
        }

        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const getMaxWordsFromPreset = (presetType) => {
        const presetMap = {
            'SHORT_ARTICLE': 300,
            'MEDIUM_ARTICLE': 600,
            'LONG_ARTICLE': 1000,
            'YOUTUBE': 350,
            'LINKEDIN': 200,
            'X': 150,
        };
        return presetMap[presetType] || 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!order?.id) {
            console.error('No project ID available');
            return;
        }

        try {
            const maxWords = formData.wordPreset?.isCustom
                ? parseInt(formData.customMaxWords) || 0
                : (formData.wordPreset ? getMaxWordsFromPreset(formData.wordPreset.type) : 0);

            const deadlineFormatted = formData.deadline
                ? `${formData.deadline}T23:59:59`
                : '';

            const payload = {
                language: safeOrder.language || null,
                textAmount: parseInt(formData.textAmount) || 0,
                numberOfImages: parseInt(formData.numberOfImages) || 0,
                title: formData.title || '',
                maxWords: maxWords,
                projectManagerId: formData.projectManager?.id || null,
                detailedBrief: formData.detailedBrief || '',
                aiAgentId: formData.aiAgent?.id ? String(formData.aiAgent.id) : (safeOrder.aiAgentId ? String(safeOrder.aiAgentId) : ''),
                aiAgentName: formData.aiName || formData.aiAgent?.name || '',
                clientDocumentation: safeOrder.clientDocumentation || '',
                semanticGap: formData.semanticGap || '',
                editorialContact: safeOrder.editorialContact || '',
                deadline: deadlineFormatted,
                contentType: formData.wordPreset?.type || safeOrder.contentType || '',
                llmType: isYoutubeAgent ? 'GPT' : (formData.llmType?.type || safeOrder.llmType || ''),
            };

            await axiosPrivate.put(`/project/${order.id}`, payload);

            if (showAllocation && writerTasks.length > 0) {
                const projectLinesBody = writerTasks
                    .filter(task => task.writer)
                    .map((task) => {
                        const isExistingLine = task.id && typeof task.id === 'number' && task.id < 1000000000;

                        return {
                            deadline: task.deadline ? `${task.deadline}T23:59:59` : null,
                            textAmount: parseInt(task.numberOfTexts) || 0,
                            minWords: parseInt(task.minWords, 10) || 0,
                            maxWords: parseInt(task.maxWords, 10) || 0,
                            userMailDTO: {
                                id: task.writer.id,
                                mail: task.writer.mail,
                            },
                            writerProjectId: isExistingLine ? task.id : null,
                        };
                    });

                if (projectLinesBody.length > 0) {
                    await axiosPrivate.put(`/project/line/${order.id}`, projectLinesBody);
                }
            }

            onClose();
            if (onUpdate && typeof onUpdate === 'function') {
                onUpdate();
            }
        } catch (error) {
            console.error('Error updating project:', error);
            const errorMessage = error?.response?.data?.message || error?.message || t('Erreur lors de la mise à jour du projet');
            alert(errorMessage);
        }
    };

    const getProjectManagerDisplay = (manager) => {
        if (!manager) return '';
        return (
            manager.mail ||
            [manager.firstName, manager.lastName].filter(Boolean).join(' ') ||
            ''
        );
    };

    const formatContentType = (ct) => {
        const key = String(ct || '').toUpperCase();
        switch (key) {
            case 'LONG_CATEGORY':
                return t('Page catégorie');
            case 'PRODUCT':
                return t('Fiche produit');
            case 'STORE_LOCATOR':
                return t('Store locator');
            case 'GMB':
                return t('GBP');
            default:
                return ct || '';
        }
    };

    const getAgentDisplay = (agent) => {
        if (!agent) return '';
        const agentName = agent.name || `${agent.type || ''} - Type ${agent.agentType || ''}`;
        const contentType = agent.typeContenu ? formatContentType(agent.typeContenu) : null;

        if (contentType) {
            return `${agentName} | ${contentType}`;
        }
        return agentName;
    };

    const getWriterDisplay = (writer) => {
        if (!writer) return '';
        return writer.mail || [writer.firstName, writer.lastName].filter(Boolean).join(' ') || writer.name || '';
    };

    // Retourne la liste des rédacteurs disponibles en excluant ceux déjà sélectionnés dans les autres tâches
    const getAvailableWritersForTask = (taskId) => {
        const selectedWriterIds = writerTasks
            .filter(task => task.id !== taskId && task.writer?.id)
            .map(task => task.writer.id);
        return availableWritersList.filter(writer => !selectedWriterIds.includes(writer.id));
    };

    const addWriterTask = () => {
        if (getRemainingTexts() <= 0) return;

        setWriterTasks([
            ...writerTasks,
            {
                id: Date.now(),
                writer: null,
                numberOfTexts: '',
                minWords: '',
                maxWords: '',
                deadline: '',
            }
        ]);
    };

    const removeWriterTask = (taskId) => {
        setWriterTasks(writerTasks.filter(task => task.id !== taskId));
    };

    const isExistingProjectLine = (task) =>
        task.id != null && typeof task.id === 'number' && task.id < 1000000000;

    const handleRemoveWriterTask = async (task) => {
        if (isExistingProjectLine(task)) {
            try {
                await axiosPrivate.delete(`/project/line/${task.id}`);
                removeWriterTask(task.id);
            } catch (error) {
                console.error('Error deleting project line:', error);
                const errorMessage = error?.response?.data?.message || error?.message || t('Erreur lors de la suppression de l\'allocation.');
                alert(errorMessage);
            }
        } else {
            removeWriterTask(task.id);
        }
    };

    const updateWriterTask = (taskId, field, value) => {
        setWriterTasks(writerTasks.map(task =>
            task.id === taskId ? { ...task, [field]: value } : task
        ));
    };

    const getTotalAllocatedTexts = () => {
        return writerTasks
            .filter(task => task.writer)
            .reduce((sum, task) => sum + (parseInt(task.numberOfTexts) || 0), 0);
    };

    const getRemainingTexts = () => {
        const total = parseInt(formData.textAmount) || 0;
        return total - getTotalAllocatedTexts();
    };

    const getDisplayedAllocatedTexts = () => {
        const totalAllocated = getTotalAllocatedTexts();
        const total = parseInt(formData.textAmount) || 0;
        return totalAllocated > total ? total : totalAllocated;
    };

    const getDisplayedRemainingTexts = () => {
        const remaining = getRemainingTexts();
        return remaining < 0 ? 0 : remaining;
    };

    return (
        <Modal
            isOpen={isOpen}
            toggle={onClose}
            size="lg"
            className="update-project-modal"
        >
            <ModalHeader toggle={onClose}>
                <div>
                    <div className="update-project-modal-header-title">
                        {t('Modifier le projet')}
                    </div>
                    <div className="update-project-modal-header-subtitle">
                        {t('Modifiez les détails de votre projet ici.')}
                    </div>
                </div>
            </ModalHeader>
            <Form onSubmit={handleSubmit}>
                <ModalBody className="update-project-modal-body">
                    {(isPremiumProject || isSmartWithReview) && (
                        <Nav tabs className="update-project-modal-tabs">
                            <NavItem>
                                <NavLink
                                    className={`update-project-tab-link ${activeTab === 'general' ? 'active' : ''}`}
                                    onClick={() => setActiveTab('general')}
                                >
                                    {t('Général')}
                                </NavLink>
                            </NavItem>
                            <NavItem>
                                <NavLink
                                    className={`update-project-tab-link ${activeTab === 'allocation' ? 'active' : ''}`}
                                    onClick={() => setActiveTab('allocation')}
                                >
                                    {t('Allocation des textes')}
                                </NavLink>
                            </NavItem>
                        </Nav>
                    )}

                    <TabContent activeTab={activeTab}>
                        <TabPane tabId="general">
                            <div className="update-project-general-form">
                                <FormGroup>
                                    <Label htmlFor="title" className="update-project-label">
                                        {t('Nom du projet')}
                                    </Label>
                                    <Input
                                        type="text"
                                        id="title"
                                        name="title"
                                        value={formData.title}
                                        onChange={handleInputChange}
                                        className="update-project-input"
                                    />
                                </FormGroup>

                                <FormGroup>
                                    <Label htmlFor="projectManager" className="update-project-label">
                                        {t('Chef de projet')}
                                    </Label>
                                    <CustomSelectDropdown
                                        options={projectManagersList}
                                        value={formData.projectManager}
                                        onChange={(selected) => handleSelectChange('projectManager', selected)}
                                        getOptionLabel={getProjectManagerDisplay}
                                        getOptionValue={(option) => option?.id}
                                        placeholder={t('Sélectionner un chef de projet')}
                                        className="update-project-input"
                                    />
                                </FormGroup>

                                <FormGroup>
                                    <Label htmlFor="deadline" className="update-project-label">
                                        {t('Date limite du projet')}
                                    </Label>
                                    <div className="update-project-deadline-wrapper">
                                        <Input
                                            type="date"
                                            id="deadline"
                                            name="deadline"
                                            value={formData.deadline}
                                            onChange={handleInputChange}
                                            className="update-project-deadline-input"
                                        />
                                    </div>
                                </FormGroup>

                                <FormGroup>
                                    <Label htmlFor="textAmount" className="update-project-label">
                                        {t('Nombre de textes')}
                                    </Label>
                                    <Input
                                        type="number"
                                        id="textAmount"
                                        name="textAmount"
                                        value={formData.textAmount}
                                        onChange={handleInputChange}
                                        className="update-project-input"
                                    />
                                    <div className="update-project-helper-text">
                                        {t('Nombre total de textes alloués au projet')}
                                    </div>
                                </FormGroup>

                                <FormGroup>
                                    <Label
                                        htmlFor="numberOfImages"
                                        className="update-project-label"
                                        style={
                                            !isImageOptionEnabled
                                                ? { color: '#9CA3AF' }
                                                : undefined
                                        }
                                    >
                                        {t("Nombre d'images")}
                                    </Label>
                                    <Input
                                        type="number"
                                        id="numberOfImages"
                                        name="numberOfImages"
                                        value={formData.numberOfImages}
                                        onChange={handleInputChange}
                                        className="update-project-input"
                                        min="0"
                                        disabled={!isImageOptionEnabled}
                                        style={
                                            !isImageOptionEnabled
                                                ? {
                                                    backgroundColor: '#F9FAFB',
                                                    cursor: 'not-allowed',
                                                    color: '#9CA3AF',
                                                }
                                                : undefined
                                        }
                                    />
                                    <div className="update-project-helper-text">
                                        {isImageOptionEnabled
                                            ? t("Nombre d'images générées par texte")
                                            : t("Option de génération d'images non activée pour ce projet")}
                                    </div>
                                </FormGroup>

                                {!isPremiumProject && (
                                    <>
                                        <FormGroup>
                                            <Label htmlFor="aiAgent" className="update-project-label">
                                                {t('Agent IA')}
                                            </Label>
                                            <CustomSelectDropdown
                                                options={aiAgentsList}
                                                value={formData.aiAgent || (safeOrder.aiAgentId ? (aiAgentsList.find(a => String(a.id) === String(safeOrder.aiAgentId)) || { id: safeOrder.aiAgentId, name: safeOrder.aiName }) : null)}
                                                onChange={(selected) => {
                                                    if (!selected) {
                                                        handleSelectChange('aiAgent', null);
                                                        return;
                                                    }
                                                    if (!aiAgentsList.find(a => a.id === selected.id)) {
                                                        handleSelectChange('aiAgent', { id: selected.id, name: safeOrder.aiName || `Agent ${selected.id}` });
                                                    } else {
                                                        handleSelectChange('aiAgent', selected);
                                                    }
                                                }}
                                                getOptionLabel={getAgentDisplay}
                                                getOptionValue={(option) => option?.id}
                                                placeholder={t('Sélectionner un agent IA')}
                                                className="update-project-input"
                                                renderOptionLabel={(agent) => {
                                                    if (!agent) return '';
                                                    const agentName = agent.name || `${agent.type || ''} - Type ${agent.agentType || ''}`;
                                                    const contentType = agent.typeContenu ? formatContentType(agent.typeContenu) : null;

                                                    if (contentType) {
                                                        return (
                                                            <span>
                                                                {agentName}{' '}
                                                                <span style={{ color: '#9CA3AF' }}>| {contentType}</span>
                                                            </span>
                                                        );
                                                    }
                                                    return agentName;
                                                }}
                                            />
                                            <div className="update-project-helper-text">
                                                {t("Vous pourrez changer d'agent plus tard")}
                                            </div>
                                        </FormGroup>

                                        <FormGroup>
                                            <Label className="update-project-label">
                                                {t('Moteur LLM')}
                                            </Label>
                                            {!isAgentContentTypeResolved && safeOrder.aiAgentId ? (
                                                <Input
                                                    type="text"
                                                    value={formData.llmType?.name || safeOrder.llmType || ''}
                                                    disabled
                                                    readOnly
                                                    className="update-project-input"
                                                    style={{
                                                        backgroundColor: '#F9FAFB',
                                                        cursor: 'not-allowed',
                                                        color: '#6B7280',
                                                    }}
                                                />
                                            ) : isYoutubeAgent ? (
                                                <Input
                                                    type="text"
                                                    value="GPT"
                                                    disabled
                                                    readOnly
                                                    className="update-project-input"
                                                    style={{
                                                        backgroundColor: '#F9FAFB',
                                                        cursor: 'not-allowed',
                                                        color: '#6B7280',
                                                    }}
                                                />
                                            ) : (
                                                <CustomSelectDropdown
                                                    options={llmTypeList}
                                                    value={formData.llmType}
                                                    onChange={(selected) =>
                                                        handleSelectChange('llmType', selected || null)
                                                    }
                                                    getOptionLabel={(option) => option?.name || ''}
                                                    getOptionValue={(option) => option?.type}
                                                    placeholder={t('Sélectionner un moteur LLM')}
                                                    className="update-project-input"
                                                    openUp={true}
                                                />
                                            )}
                                        </FormGroup>
                                    </>
                                )}

                                <FormGroup>
                                    <Label className="update-project-label">
                                        {t('Format du contenu')}
                                    </Label>
                                    <CustomSelectDropdown
                                        options={contentFormatList}
                                        value={formData.wordPreset}
                                        onChange={(selected) => handleSelectChange('wordPreset', selected || null)}
                                        getOptionLabel={(option) => option?.name || option}
                                        getOptionValue={(option) => option?.type || option}
                                        placeholder={t('Sélectionner un format du contenu')}
                                        className="update-project-input"
                                        openUp={true}
                                        renderOptionLabel={(option) => {
                                            if (option?.isCustom) {
                                                return (
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                        <img src="/Images/perso-icon.png" alt="Personnalisé" style={{ width: '16px', height: '16px' }} />
                                                        <span>{option.name}</span>
                                                    </div>
                                                );
                                            }
                                            return option?.name || '';
                                        }}
                                    />
                                    <div className="update-project-helper-text">
                                        {t('Vous pourrez changer le format du contenu plus tard')}
                                    </div>
                                </FormGroup>

                                {formData.wordPreset?.isCustom && (
                                    <FormGroup>
                                        <Label className="update-project-label">
                                            {t('Nombre de mots par texte')}
                                        </Label>
                                        <div className="update-project-custom-words-wrapper">
                                            <div className="update-project-custom-words-container">
                                                <div className="update-project-custom-word-field">
                                                    <div className="update-project-custom-word-label">Min</div>
                                                    <Input
                                                        type="number"
                                                        name="customMinWords"
                                                        value={formData.customMinWords}
                                                        onChange={handleInputChange}
                                                        placeholder={t('Ex: 500')}
                                                        className="update-project-custom-word-input"
                                                        min="1"
                                                    />
                                                </div>
                                                <div className="update-project-custom-word-field">
                                                    <div className="update-project-custom-word-label">Max</div>
                                                    <Input
                                                        type="number"
                                                        name="customMaxWords"
                                                        value={formData.customMaxWords}
                                                        onChange={handleInputChange}
                                                        placeholder={t('Ex: 1000')}
                                                        className="update-project-custom-word-input"
                                                        min="1"
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </FormGroup>
                                )}

                                {/* Liens utiles */}
                                <FormGroup>
                                    <Label className="update-project-label">
                                        {t('Liens utiles')}{' '}
                                        <span style={{ color: '#9CA3AF', fontSize: 12, fontWeight: 400 }}>
                                            ({t('optionnel')})
                                        </span>
                                    </Label>
                                    <div className="editorial-context-block" style={{ marginTop: 4 }}>
                                        <div className="form-fields">
                                            <div className="form-field form-field-bottom-border">
                                                <div className="field-label-wrapper">
                                                    <label className="field-label">{t('Gap sémantique')}</label>

                                                </div>
                                                <div className="drive-input-wrapper">
                                                    <div className="drive-input-icon">
                                                        <img src="/Images/drive-icon.png" alt="Google Drive" />
                                                    </div>
                                                    <input
                                                        type="text"
                                                        className="drive-input"
                                                        placeholder={t('Lien drive...')}
                                                        name="semanticGap"
                                                        value={formData.semanticGap}
                                                        onChange={handleInputChange}
                                                    />
                                                </div>
                                            </div>
                                            <div className="form-field">
                                                <div className="field-label-wrapper">
                                                    <label className="field-label">{t('Brief détaillé')}</label>

                                                </div>
                                                <div className="drive-input-wrapper">
                                                    <div className="drive-input-icon">
                                                        <img src="/Images/drive-icon.png" alt="Google Drive" />
                                                    </div>
                                                    <input
                                                        type="text"
                                                        className="drive-input"
                                                        placeholder={t('Lien drive...')}
                                                        name="detailedBrief"
                                                        value={formData.detailedBrief}
                                                        onChange={handleInputChange}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </FormGroup>
                            </div>
                        </TabPane>

                        <TabPane tabId="allocation">
                            {showAllocation ? (
                                <div className="update-project-allocation-content">
                                    <div className="update-project-allocation-header">
                                        <div>
                                            <h3 className="update-project-allocation-title">
                                                {t('Allocation aux rédacteurs')}
                                            </h3>
                                            <p className="update-project-allocation-subtitle">
                                                {t('{{count}} textes disponibles', { count: formData.textAmount || 0 })}
                                            </p>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={addWriterTask}
                                            className="update-project-add-writer-btn"
                                            disabled={getRemainingTexts() <= 0}
                                            title={getRemainingTexts() <= 0 ? t('Nombre de textes alloués atteint') : undefined}
                                            style={getRemainingTexts() <= 0 ? { opacity: 0.4, cursor: 'not-allowed' } : undefined}
                                        >
                                            <span>+</span> {t('Ajouter')}
                                        </button>
                                    </div>

                                    <div className="update-project-allocation-summary">
                                        <div className="update-project-allocation-summary-text">
                                            <strong>{getDisplayedAllocatedTexts()} / {formData.textAmount || 0} {t('textes alloués')}</strong>
                                        </div>
                                        <div className="update-project-allocation-summary-remaining">
                                            {getDisplayedRemainingTexts()} {t('texte restant')}
                                        </div>
                                    </div>
                                    {writerTasks.map((task, index) => (
                                        <div key={task.id} className="update-project-writer-card">
                                            <button
                                                type="button"
                                                onClick={() => handleRemoveWriterTask(task)}
                                                className="update-project-writer-delete-btn"
                                            >
                                                <img src="/Images/icon-trash.png" alt="delete" style={{ width: 16, height: 16 }} />
                                            </button>
                                            <FormGroup className="update-project-writer-form-group">
                                                <Label className="update-project-label">
                                                    {t('Nom du rédacteur')}
                                                </Label>
                                                <CustomSelectDropdown
                                                    options={getAvailableWritersForTask(task.id)}
                                                    value={task.writer}
                                                    onChange={(selected) => updateWriterTask(task.id, 'writer', selected)}
                                                    getOptionLabel={getWriterDisplay}
                                                    getOptionValue={(option) => option?.id}
                                                    placeholder={t('Sélectionner un rédacteur')}
                                                    className="update-project-input"
                                                    inputStyle={{
                                                        minHeight: '32px',
                                                        height: '32px',
                                                        padding: '4px 8px',
                                                        paddingRight: '2.5rem',
                                                        fontSize: '13px',
                                                    }}
                                                />
                                            </FormGroup>
                                            <FormGroup className="update-project-writer-form-group">
                                                <Label className="update-project-label">
                                                    {t('Nombre de textes')}
                                                </Label>
                                                <Input
                                                    type="number"
                                                    value={task.numberOfTexts}
                                                    onChange={(e) => updateWriterTask(task.id, 'numberOfTexts', e.target.value)}
                                                    className={`update-project-input${getRemainingTexts() < 0 ? ' update-project-input-error' : ''}`}
                                                    min="1"
                                                />
                                                {getRemainingTexts() < 0 && index === writerTasks.length - 1 && (
                                                    <div className="update-project-field-error">
                                                        {t('Nombre de textes maximum atteint.')}<br />
                                                        {t('Vous avez réparti tous les textes disponibles pour ce projet.')}
                                                    </div>
                                                )}
                                            </FormGroup>
                                            <FormGroup className="update-project-writer-form-group">
                                                <div
                                                    style={{
                                                        fontSize: '12px',
                                                        fontWeight: 600,
                                                        color: '#4B5563',
                                                        marginBottom: '8px',
                                                    }}
                                                >
                                                    {t('Nombre de mots')}
                                                </div>
                                                <div style={{ display: 'flex', gap: '12px', width: '100%' }}>
                                                    <div style={{ flex: 1, display: 'flex', alignItems: 'center' }}>
                                                        <div
                                                            style={{
                                                                padding: '0 10px',
                                                                height: '32px',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                border: '1px solid #E5E7EB',
                                                                borderRight: 'none',
                                                                borderRadius: '6px 0 0 6px',
                                                                fontSize: '12px',
                                                                color: '#6B7280',
                                                                backgroundColor: '#F9FAFB',
                                                            }}
                                                        >
                                                            Min
                                                        </div>
                                                        <Input
                                                            type="number"
                                                            value={task.minWords ?? ''}
                                                            onChange={(e) => updateWriterTask(task.id, 'minWords', e.target.value)}
                                                            min="0"
                                                            placeholder="0"
                                                            style={{
                                                                flex: 1,
                                                                height: '32px',
                                                                borderRadius: '0 6px 6px 0',
                                                                border: '1px solid #E5E7EB',
                                                                borderLeft: 'none',
                                                                padding: '0 12px',
                                                                fontSize: '13px',
                                                            }}
                                                        />
                                                    </div>
                                                    <div style={{ flex: 1, display: 'flex', alignItems: 'center' }}>
                                                        <div
                                                            style={{
                                                                padding: '0 10px',
                                                                height: '32px',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                border: '1px solid #E5E7EB',
                                                                borderRight: 'none',
                                                                borderRadius: '6px 0 0 6px',
                                                                fontSize: '12px',
                                                                color: '#6B7280',
                                                                backgroundColor: '#F9FAFB',
                                                            }}
                                                        >
                                                            Max
                                                        </div>
                                                        <Input
                                                            type="number"
                                                            value={task.maxWords ?? ''}
                                                            onChange={(e) => updateWriterTask(task.id, 'maxWords', e.target.value)}
                                                            min="0"
                                                            placeholder="0"
                                                            style={{
                                                                flex: 1,
                                                                height: '32px',
                                                                borderRadius: '0 6px 6px 0',
                                                                border: '1px solid #E5E7EB',
                                                                borderLeft: 'none',
                                                                padding: '0 12px',
                                                                fontSize: '13px',
                                                            }}
                                                        />
                                                    </div>
                                                </div>
                                            </FormGroup>
                                            <FormGroup className="update-project-writer-form-group">
                                                <Label className="update-project-label">
                                                    {t('Date limite')}
                                                </Label>
                                                <div className="update-project-deadline-wrapper">
                                                    <Input
                                                        type="date"
                                                        value={task.deadline}
                                                        onChange={(e) => updateWriterTask(task.id, 'deadline', e.target.value)}
                                                        className="update-project-deadline-input"
                                                    />
                                                </div>
                                            </FormGroup>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="update-project-allocation-placeholder">
                                    {t('Contenu à venir - Allocation des textes')}
                                </div>
                            )}
                        </TabPane>
                    </TabContent>
                </ModalBody>
                <ModalFooter className="update-project-modal-footer">
                    <button
                        type="button"
                        onClick={onClose}
                        className="update-project-btn-cancel"
                    >
                        {t('Annuler')}
                    </button>
                    <button
                        type="submit"
                        className="update-project-btn-save"
                    >
                        {t('Enregistrer')}
                    </button>
                </ModalFooter>
            </Form>
        </Modal>
    );
};

export default UpdateProjectModal;